import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Users, 
  Gift, 
  Calendar,
  ExternalLink,
  MoreHorizontal,
  Trash2,
  Edit2,
  Eye,
  Play,
  Clock,
  CheckCircle2,
  AlertCircle,
  Timer,
  CalendarClock,
  CalendarCheck,
  Hourglass
} from "lucide-react";
import { formatDate } from "../lib/utils";

// Hook pour le compte à rebours en temps réel
function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState(null);
  
  useEffect(() => {
    if (!targetDate) return;
    
    const calculateTimeLeft = () => {
      const now = new Date();
      const target = new Date(targetDate);
      const diff = target - now;
      
      if (diff <= 0) return null;
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      return { days, hours, minutes, seconds, total: diff };
    };
    
    setTimeLeft(calculateTimeLeft());
    
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearInterval(timer);
  }, [targetDate]);
  
  return timeLeft;
}

// Composant de compte à rebours
function Countdown({ targetDate, label, variant = "default" }) {
  const timeLeft = useCountdown(targetDate);
  
  if (!timeLeft) return null;
  
  const formatTime = () => {
    if (timeLeft.days > 0) return `${timeLeft.days}j ${timeLeft.hours}h ${timeLeft.minutes}min`;
    if (timeLeft.hours > 0) return `${timeLeft.hours}h ${timeLeft.minutes}min ${timeLeft.seconds}s`;
    if (timeLeft.minutes > 0) return `${timeLeft.minutes}min ${timeLeft.seconds}s`;
    return `${timeLeft.seconds}s`;
  };
  
  const isUrgent = timeLeft.total < 24 * 60 * 60 * 1000; // Moins de 24h
  const isCritical = timeLeft.total < 60 * 60 * 1000; // Moins de 1h
  
  const colorClass = isCritical 
    ? "text-red-600 dark:text-red-400" 
    : isUrgent 
      ? "text-amber-600 dark:text-amber-400"
      : "text-primary";
  
  return (
    <div className={`flex items-center gap-1.5 text-xs font-medium ${colorClass}`}>
      <Hourglass className={`w-3.5 h-3.5 ${isCritical ? "animate-pulse" : ""}`} />
      <span>{label} {formatTime()}</span>
    </div>
  );
}

export function DrawCard({ draw, onDelete, onNavigate }) {
  const now = new Date();
  const drawDate = draw.draw_date ? new Date(draw.draw_date) : null;
  const participationDeadline = draw.participation_deadline ? new Date(draw.participation_deadline) : null;
  
  // Determine if the event is finished/closed
  const isCompleted = draw.status === "completed";
  const isClosed = draw.status === "closed" || (drawDate && drawDate < now && !isCompleted);
  const isFinished = isCompleted || isClosed;
  
  const getStatusConfig = () => {
    if (isCompleted) {
      return { 
        badge: <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Terminé</Badge>,
        icon: <CheckCircle2 className="w-4 h-4 text-green-500" />,
        canEdit: false
      };
    }
    if (isClosed) {
      return { 
        badge: <Badge className="bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400">Clôturé</Badge>,
        icon: <Clock className="w-4 h-4 text-slate-500" />,
        canEdit: false
      };
    }
    if (!draw.is_paid && draw.is_free_used !== false) {
      return { 
        badge: <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">Inclus</Badge>,
        icon: <AlertCircle className="w-4 h-4 text-blue-500" />,
        canEdit: true
      };
    }
    return { 
      badge: <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">Prêt</Badge>,
      icon: <Play className="w-4 h-4 text-blue-500" />,
      canEdit: true
    };
  };

  const status = getStatusConfig();
  const hasDrawDate = drawDate && drawDate > now;
  const hasParticipationDeadline = participationDeadline && participationDeadline > now && !isFinished;

  // Card styling based on status
  const cardClasses = isFinished
    ? "bg-card/60 hover:shadow-sm transition-all group overflow-hidden grayscale-[30%] opacity-80"
    : "bg-card hover:shadow-md transition-all group overflow-hidden";

  return (
    <Card className={cardClasses} data-testid={`draw-card-${draw.id}`}>
      {/* Colored top bar based on status */}
      <div className={`h-1 ${draw.status === "completed" ? "bg-green-500" : draw.is_paid ? "bg-blue-500" : "bg-blue-400"}`} />
      
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground line-clamp-1 text-base">
              {draw.title}
            </h3>
            <p className="text-xs text-muted-foreground truncate">{draw.organizer_name}</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onNavigate(`/draws/${draw.id}`)}>
                <Edit2 className="w-4 h-4 mr-2" />
                Modifier
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`/draw/${draw.id}/register`, '_blank')}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Page participation
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`/draw/${draw.id}/results`, '_blank')}>
                <Eye className="w-4 h-4 mr-2" />
                Page résultats
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-destructive"
                onClick={() => onDelete(draw.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Stats row */}
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Users className="w-4 h-4" />
            <span className="font-medium text-foreground">{draw.participants?.length || 0}</span>
          </span>
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Gift className="w-4 h-4" />
            <span className="font-medium text-foreground">{draw.prizes?.length || 0}</span>
          </span>
        </div>

        {/* Dates section */}
        <div className="space-y-1.5 p-2 rounded-lg bg-muted/50">
          {/* Start date / Created */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <CalendarClock className="w-3.5 h-3.5 shrink-0" />
            {draw.start_date ? (
              <span>Début : <span className="text-foreground font-medium">{formatDate(draw.start_date)}</span></span>
            ) : (
              <span>Créé le <span className="text-foreground">{formatDate(draw.created_at)}</span></span>
            )}
          </div>
          
          {/* Participation deadline */}
          {draw.participation_deadline && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Timer className="w-3.5 h-3.5 shrink-0" />
              <span>Limite participation : <span className="text-foreground font-medium">{formatDate(draw.participation_deadline)}</span></span>
            </div>
          )}
          
          {/* Draw date with countdown */}
          {draw.draw_date && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CalendarCheck className="w-3.5 h-3.5 shrink-0" />
              {isCompleted ? (
                <span>Tiré le <span className="text-foreground font-medium">{formatDate(draw.draw_timestamp || draw.draw_date)}</span></span>
              ) : isClosed ? (
                <span className="text-slate-500">Date passée : <span className="font-medium">{formatDate(draw.draw_date)}</span></span>
              ) : (
                <span>Tirage : <span className="text-foreground font-medium">{formatDate(draw.draw_date)}</span></span>
              )}
            </div>
          )}
          
          {/* Live countdown for participation deadline */}
          {hasParticipationDeadline && (
            <Countdown targetDate={draw.participation_deadline} label="Participation ferme dans" />
          )}
          
          {/* Live countdown for draw date */}
          {hasDrawDate && !isCompleted && !hasParticipationDeadline && (
            <Countdown targetDate={draw.draw_date} label="Tirage dans" />
          )}
          
          {/* Finished indicator */}
          {isFinished && (
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 mt-1 pt-1 border-t border-muted">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>{isCompleted ? "Tirage effectué" : "Événement terminé"}</span>
            </div>
          )}
        </div>

        {/* Status and actions */}
        <div className="flex items-center justify-between pt-2 border-t">
          {status.badge}
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-xs"
              onClick={() => onNavigate(`/draws/${draw.id}`)}
            >
              <Edit2 className="w-3.5 h-3.5 mr-1.5" />
              Gérer
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function PollCard({ poll, onDelete, onNavigate }) {
  const now = new Date();
  const startDate = poll.start_date ? new Date(poll.start_date) : null;
  const endDate = poll.end_date ? new Date(poll.end_date) : null;
  const participationDeadline = poll.participation_deadline ? new Date(poll.participation_deadline) : null;
  
  // Determine if the poll is finished/closed
  const isClosed = poll.status === "closed" || (endDate && endDate < now);
  const isFinished = isClosed;
  
  const hasEndDate = endDate && endDate > now && poll.status === "active";
  const hasStartDate = startDate && startDate > now;
  const hasParticipationDeadline = participationDeadline && participationDeadline > now && !isFinished;

  const getStatusConfig = () => {
    if (isClosed) {
      return { 
        badge: <Badge className="bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400">Fermé</Badge>
      };
    }
    if (poll.status === "active") {
      return { 
        badge: <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">En cours</Badge>
      };
    }
    // Draft or scheduled
    if (hasStartDate) {
      return { 
        badge: <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">Programmé</Badge>
      };
    }
    return { 
      badge: <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">Brouillon</Badge>
    };
  };

  const status = getStatusConfig();
  const totalVotes = poll.options?.reduce((sum, opt) => sum + (opt.votes || 0), 0) || 0;

  // Card styling based on status
  const cardClasses = isFinished
    ? "bg-card/60 hover:shadow-sm transition-all group overflow-hidden grayscale-[30%] opacity-80"
    : "bg-card hover:shadow-md transition-all group overflow-hidden";

  return (
    <Card className={cardClasses} data-testid={`poll-card-${poll.id}`}>
      {/* Colored top bar */}
      <div className={`h-1 ${poll.status === "active" ? "bg-green-500" : poll.status === "closed" ? "bg-slate-400" : "bg-purple-500"}`} />
      
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground line-clamp-1 text-base">
              {poll.title}
            </h3>
            <p className="text-xs text-muted-foreground truncate">{poll.organizer_name}</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onNavigate(`/polls/${poll.id}`)}>
                <Edit2 className="w-4 h-4 mr-2" />
                Modifier
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`/poll/${poll.id}/vote`, '_blank')}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Page de vote
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`/poll/${poll.id}/results`, '_blank')}>
                <Eye className="w-4 h-4 mr-2" />
                Page résultats
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-destructive"
                onClick={() => onDelete(poll.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Stats row */}
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Users className="w-4 h-4" />
            <span className="font-medium text-foreground">{totalVotes}</span> votes
          </span>
          <span className="text-muted-foreground">
            {poll.options?.length || 0} options
          </span>
        </div>

        {/* Dates section */}
        <div className="space-y-1.5 p-2 rounded-lg bg-muted/50">
          {/* Start date */}
          {startDate && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CalendarClock className="w-3.5 h-3.5 shrink-0" />
              <span>Début : <span className="text-foreground font-medium">{formatDate(poll.start_date)}</span></span>
            </div>
          )}
          
          {/* Participation deadline */}
          {poll.participation_deadline && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Timer className="w-3.5 h-3.5 shrink-0" />
              <span>Limite participation : <span className="text-foreground font-medium">{formatDate(poll.participation_deadline)}</span></span>
            </div>
          )}
          
          {/* End date */}
          {endDate && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CalendarCheck className="w-3.5 h-3.5 shrink-0" />
              {isFinished ? (
                <span className="text-slate-500">Terminé le : <span className="font-medium">{formatDate(poll.end_date)}</span></span>
              ) : (
                <span>Fin : <span className="text-foreground font-medium">{formatDate(poll.end_date)}</span></span>
              )}
            </div>
          )}
          
          {/* If no dates, show created date */}
          {!startDate && !endDate && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="w-3.5 h-3.5 shrink-0" />
              <span>Créé le <span className="text-foreground">{formatDate(poll.created_at)}</span></span>
            </div>
          )}
          
          {/* Live countdown for participation deadline */}
          {hasParticipationDeadline && (
            <Countdown targetDate={poll.participation_deadline} label="Participation ferme dans" />
          )}
          
          {/* Live countdown for end date */}
          {hasEndDate && !hasParticipationDeadline && (
            <Countdown targetDate={poll.end_date} label="Fin dans" />
          )}
          {hasStartDate && poll.status !== "active" && (
            <Countdown targetDate={poll.start_date} label="Ouvre dans" />
          )}
          
          {/* Finished indicator */}
          {isFinished && (
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 mt-1 pt-1 border-t border-muted">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Sondage terminé</span>
            </div>
          )}
        </div>

        {/* Status and actions */}
        <div className="flex items-center justify-between pt-2 border-t">
          {status.badge}
          
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 text-xs"
            onClick={() => onNavigate(`/polls/${poll.id}`)}
          >
            <Edit2 className="w-3.5 h-3.5 mr-1.5" />
            Gérer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
